<template>
  <div class="c-questionnaire-banner">
    <div class="c-questionnaire-banner__info">
      <div class="c-questionnaire-banner__spacer"/>
      <div class="c-questionnaire-banner__body">
        <div class="c-questionnaire-banner__title" v-html="title"/>
        <div v-if="descriptions" class="c-questionnaire-banner__descriptions" v-html="descriptions"/>
        <template v-if="warning && warning !== ''">
          <div class="c-questionnaire-banner__button is-warning">
            <c-button
              :text="buttonText"
              adaptive="block"
              size="large"
              @click="onClick"
            />
          </div>
          <div class="c-questionnaire-banner__mobile">
            <c-dialog
              :bar="[{ text: buttonText, appearance: 'contained', adaptive: 'block', size: 'large' }]"
              @on-click="onClick"
            >
              <template v-slot:title>
                <div class="c-questionnaire-banner__mobile__title" v-html="warningTitle"/>
              </template>
              <template v-slot:control="{ classList, onClick }">
                <c-button
                  :class="classList"
                  :text="buttonWarning"
                  adaptive="block"
                  size="large"
                  @click="onClick"
                />
              </template>
              <template v-slot:default>
                <div class="c-questionnaire-banner__mobile__warning" v-html="warning"/>
              </template>
            </c-dialog>
          </div>
        </template>
        <div v-else class="c-questionnaire-banner__button">
          <c-button
            :text="buttonText"
            adaptive="block"
            size="large"
            @click="onClick"
          />
        </div>
      </div>
      <div class="c-questionnaire-banner__spacer c-questionnaire-banner__warning is-warning" v-html="warning"/>
    </div>
    <div class="c-questionnaire-banner__image">
      <d-icon-item :icon="image"/>
    </div>
  </div>
</template>

<script>
import CButton from './CButton'
import CDialog from './CDialog'
import DIconItem from 'dcode/components/DIconItem'

export default {
  name: 'CQuestionnaireBanner',
  components: {
    CButton,
    CDialog,
    DIconItem
  },
  emits: ['on-click'],
  props: {
    // Values
    title: String,
    descriptions: String,
    warning: String,
    warningTitle: String,
    buttonText: String,
    buttonWarning: String,
    image: String,

    // Server
    name: String,
    item: Object
  },
  setup (props, context) {
    return {
      onClick () {
        context.emit('on-click', {
          item: { ...props.item },
          name: props.name
        })
      }
    }
  }
}
</script>

<style lang="scss">
@import "~dcode/styles/flex";
@import "~dcode/styles/font";
@import "~dcode/styles/margin";
@import "~dcode/styles/media";
@import "~dcode/styles/padding";

.c-questionnaire-banner {
  --_qb-padding-top: var(--qb-padding-top, 72px);
  --_qb-padding-top-md: var(--qb-padding-top-md, 120px);
  --_qb-padding-top-ds: var(--qb-padding-top-ds, 0px);
  --_qb-padding-left: var(--qb-padding-left, 16px);
  --_qb-padding-left-ds: var(--qb-padding-left-ds, 32px);
  --_qb-padding-left-ex: var(--qb-padding-left-ex, 108px);
  --_qb-padding-right: var(--qb-padding-right, 16px);
  --_qb-padding-right-ds: var(--qb-padding-right-ds, 32px);
  --_qb-padding-right-lr: var(--qb-padding-right-lr, 12px);
  --_qb-padding-right-ex: var(--qb-padding-right-ex, 42px);
  --_qb-ds-font-size: var(--qb-ds-font-size, 14px);
  --_qb-ds-font-size-md: var(--qb-ds-font-size-md, 16px);
  --_qb-ds-font-size-ds: var(--qb-ds-font-size-ds, 18px);
  --_qb-ds-font-height: var(--qb-ds-font-height, 17px);
  --_qb-ds-font-height-md: var(--qb-ds-font-height-md, 20px);
  --_qb-ds-font-height-ds: var(--qb-ds-font-height-ds, 24px);
  --_qb-ds-padding: var(--qb-ds-padding, 16px);
  --_qb-ds-padding-ds: var(--qb-ds-padding-ds, 24px);
  --_qb-bt-width: var(--qb-bt-width, 100%);
  --_qb-bt-width-md: var(--qb-bt-width-md, 344px);
  --_qb-bt-width-ex: var(--qb-bt-width-ex, 240px);
  --_qb-wr-padding-bottom: var(--qb-wr-padding-bottom, 48px);
  --_qb-wr-padding-bottom-lr: var(--qb-wr-padding-bottom-lr, 40px);
  --_qb-wr-padding-bottom-ex: var(--qb-wr-padding-bottom-ex, 48px);

  display: flex;
  flex-direction: column;

  width: 100%;
  height: 100%;

  > * {
    flex-basis: 50%;
  }

  &__info {
    @include paddingDirectionX(var(--_qb-padding-left), var(--_qb-padding-right));
    flex-direction: column;
    padding-top: var(--_qb-padding-top);
    text-align: center;
    width: 100%;
  }

  &__title {
    font-weight: 700;
    font-size: 24px;
    line-height: 28px;
  }

  &__descriptions {
    @include font('subtitle2');
    @include paddingY(var(--_qb-ds-padding));
    font-size: var(--_qb-ds-font-size);
    line-height: var(--_qb-ds-font-height);
  }

  &__warning {
    @include font('caption');
    --basic-text-opacity: var(--opacity-surface-medium);
    padding-bottom: var(--_qb-wr-padding-bottom);
    align-items: flex-end;
  }

  &__spacer {
    display: none;
    flex-basis: 1px;
    flex-grow: 1;

    &:not(.is-warning) {
      padding-top: var(--_qb-wr-padding-bottom);
    }
  }

  &__button {
    padding-top: var(--_qb-ds-padding);
    max-width: var(--_qb-bt-width);

    &.is-warning {
      display: none;
    }
  }

  &__mobile {
    padding-top: var(--_qb-ds-padding);

    &__warning {
      --basic-text-opacity: var(--opacity-surface-medium);
      padding-bottom: 16px;
    }
  }

  &__image {
    background-color: #C4C4C4;
    position: relative;
  }

  @include mediaMinWidth {
    --_qb-padding-top: var(--_qb-padding-top-md);
    --_qb-padding-left: var(--_qb-padding-left-ds);
    --_qb-padding-right: var(--_qb-padding-right-ds);
    --_qb-ds-font-size: var(--_qb-ds-font-size-md);
    --_qb-ds-font-height: var(--_qb-ds-font-height-md);
    --_qb-bt-width: var(--_qb-bt-width-md);

    &__title {
      font-size: 32px;
      line-height: 36px;
    }

    &__info {
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
      text-align: left;
    }

    &__descriptions {
      padding-top: var(--_qb-ds-padding-ds);
    }

    &__spacer.is-warning {
      display: flex;
    }

    &__button {

      &.is-warning {
        display: block;
      }
    }

    &__mobile {
      display: none;
    }
  }

  @include mediaMinWidth(large) {
    --_qb-padding-top: var(--_qb-padding-top-ds);
    --_qb-padding-right: var(--_qb-padding-right-lr);
    --_qb-wr-padding-bottom: var(--_qb-wr-padding-bottom-lr);
    --_qb-ds-font-size: var(--_qb-ds-font-size-ds);
    --_qb-ds-font-height: var(--_qb-ds-font-height-ds);
    --_qb-bt-width: var(--_qb-bt-width-ex);
    flex-direction: row;

    &__info {
    }

    &__title {
      font-size: 40px;
      line-height: 48px;
    }

    &__spacer {
      display: block;
    }
  }

  @include mediaMinWidth(extra) {
    --_qb-padding-left: var(--_qb-padding-left-ex);
    --_qb-padding-right: var(--_qb-padding-right-ex);
    --_qb-wr-padding-bottom: var(--_qb-wr-padding-bottom-ex);
  }
}
</style>
